import { Box, Divider, Typography } from '@mui/material';
import { useAtomValue } from 'jotai';
import { useMemo, useState } from 'react';
import { GenericAccordion } from '@/components/shared/GenericAccordion';
import * as state from '@/components/Analysis/state';
import {
  BlisterHeightType,
  Maybe,
  UnitsEnum,
  useStructureWallLossFactorQuery,
} from '@/__generated__/graphql';

import { FieldViewer } from '../../FieldViewer';
import {
  AitUserVerifiedPoiView,
  InspectorBlisterEditIconButton,
  InspectorVerifiedFieldsAccordian,
  InspectorVerifiedPoiView,
} from './DetailsTabViews';
import { PointOfInterest, PointOfInterestTemplate } from '@/__generated__/graphql';
import {
  convertInchesToMillimeters,
  convertMillimetersToInches,
  formatPercentage,
  localiseMeasurement,
} from '@/utils/unitSystem';
import { useFeatureFlag } from '@/hooks';
import { TemplateFieldTypeEnum } from '@/types';
import {
  UNCERTAIN_PROFILE_LABEL,
  PIT_DEPTH_LABEL,
  BLISTER_HEIGHT_LABEL,
  BLISTER_TYPE,
  PIT_TYPE,
  CALCULATION_METHOD_LABEL,
  heightTypeMapping,
  BLISTER_TOOL_HEIGHT_LABEL,
  PDR_HEIGHT_LABEL,
  SC_CATEGORY_KEY,
} from '@/constants';
import { AttachmentsViewer } from '../AttachmentsViewer';
import { UnitSystemEnum } from '@/__generated__/graphql';
import { Attachment } from '../../types';
import { MeasureAutoPointOfInterest } from '../MeasureAutoPointOfInterest';
import { useNavbarHeight } from '@/hooks/useNavbarHeight';
import { useGetStructureId } from '@/hooks/useGetStructureId';
import { BlisterHeightMethodSelector } from './BlisterHeightMethodSelector';
import { getBlisterHeightValueDisplay } from '@/utils/pointOfInterest';
import { useIsPoiWorkScope } from '@/hooks/useIsPoiWorkScope';

const accordianProps = {
  headingStyles: {
    fontWeight: 'bold',
  },
};

type Props = {
  disableEdit?: boolean;
  selectedPoiInfo: PointOfInterest;
};

const boxRootStyle = {
  paddingBottom: '2.4rem',
  overflowY: 'auto',
  msOverflowStyle: 'none',
  scrollbarWidth: 'none',
  '&::-webkit-scrollbar': { display: 'none' },
};

type BlisterHeightFieldsProps = {
  isAutoPOI: boolean;
  unitSystem: UnitSystemEnum;
  estimatedHeight?: Maybe<number>;
  pdrHeight?: Maybe<number>;
  heightTypeValue: string;
};

type FieldItem = {
  value: string;
  label: string;
};

const getBlisterHeightFields = ({
  isAutoPOI,
  unitSystem,
  estimatedHeight,
  pdrHeight,
  heightTypeValue,
}: BlisterHeightFieldsProps): FieldItem[] => {
  const result: FieldItem[] = [];

  // Add estimated height and PDR height fields for auto-generated POIs
  if (isAutoPOI) {
    // Add blister tool height field
    result.push({
      value: getBlisterHeightValueDisplay(unitSystem, estimatedHeight),
      label: BLISTER_TOOL_HEIGHT_LABEL,
    });

    // Add PDR height field
    result.push({
      value: getBlisterHeightValueDisplay(unitSystem, pdrHeight),
      label: PDR_HEIGHT_LABEL,
    });
  }

  // Add calculation method field if height type is not uncertain
  if (isAutoPOI && heightTypeValue !== heightTypeMapping[BlisterHeightType.Uncertain]) {
    result.push({
      value: heightTypeValue,
      label: CALCULATION_METHOD_LABEL,
    });
  }

  return result;
};

export const DetailsTab = ({ disableEdit, selectedPoiInfo }: Props) => {
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const allPointOfInterestTemplates = useAtomValue(state.allPointOfInterestTemplates);
  const structureBlisterHeightType = useAtomValue(state.structureBlisterHeightType);
  const selectedAssemblyId = useAtomValue(state.selectedAssemblyId);
  const unitSystem = useAtomValue(state.unitSystem);
  const navbarHeight = useNavbarHeight();
  const structureId = useGetStructureId();
  const { checkByType } = useIsPoiWorkScope();

  const { data: structureData } = useStructureWallLossFactorQuery({
    variables: { structureId },
    skip: !structureId,
  });

  const structureWallLossFactor = useMemo(() => {
    const wallLossFactor = structureData?.structure?.wallLossFactor;
    if (!wallLossFactor) return 'N/A';
    return String(wallLossFactor);
  }, [structureData]);

  const templateOfSelectedPoi = useMemo(() => {
    return allPointOfInterestTemplates.find(({ id }) => id === selectedPoiInfo.template?.id);
  }, [allPointOfInterestTemplates, selectedPoiInfo]);

  // Need to render the comments separetely
  const templateFields = useMemo(() => {
    return (
      templateOfSelectedPoi?.fields?.filter(
        (field) => !field.isHidden && field.name !== 'Comments'
      ) || []
    );
  }, [templateOfSelectedPoi]);

  const templateFieldsWithValues: Array<{
    label: string;
    value: string;
    type?: TemplateFieldTypeEnum;
    valueTooltip?: string;
    unit?: UnitsEnum | null | undefined;
  }> = useMemo(() => {
    let blisterInfo: { value: string; label: string }[] = [];
    if (selectedPoiInfo.blister) {
      const {
        maxHeight,
        wallLoss,
        residualCorrosionAllowance,
        residualThickness,
        heightType,
        isManuallyOverridden,
        estimatedHeight,
        pdrHeight,
      } = selectedPoiInfo.blister || {};
      const isAutoPOI = selectedPoiInfo.isAutoGenerated;
      const enableWallLossFactor = selectedPoiInfo.type !== PIT_TYPE;
      const label = selectedPoiInfo.type === BLISTER_TYPE ? BLISTER_HEIGHT_LABEL : PIT_DEPTH_LABEL;
      const heightTypeValue = isManuallyOverridden
        ? heightTypeMapping[heightType ?? BlisterHeightType.BlisterTool]
        : heightTypeMapping[structureBlisterHeightType ?? BlisterHeightType.BlisterTool];

      blisterInfo = [
        ...getBlisterHeightFields({
          isAutoPOI: isAutoPOI ?? false,
          unitSystem,
          estimatedHeight,
          pdrHeight,
          heightTypeValue,
        }),

        {
          value:
            localiseMeasurement({
              unitSystem,
              value: maxHeight || undefined,
              showUnitLabel: true,
              sourceUnit: UnitsEnum.Meter,
              displayAsUnit: UnitsEnum.Millimeter,
              fallbackLabel: UNCERTAIN_PROFILE_LABEL,
            }) ?? UNCERTAIN_PROFILE_LABEL,
          label: label,
        },
        {
          value:
            localiseMeasurement({
              unitSystem,
              value: wallLoss?.value ?? 0,
              showUnitLabel: true,
              sourceUnit: UnitsEnum.Meter,
              displayAsUnit: UnitsEnum.Millimeter,
            }) + formatPercentage(wallLoss?.percentage),
          label: 'Estimated Wall Loss',
        },
        ...(enableWallLossFactor
          ? [{ value: structureWallLossFactor, label: 'Estimated Wall Loss Factor' }]
          : []),
        {
          value:
            localiseMeasurement({
              unitSystem,
              value: residualCorrosionAllowance?.value ?? 0,
              showUnitLabel: true,
              sourceUnit: UnitsEnum.Meter,
              displayAsUnit: UnitsEnum.Millimeter,
            }) + formatPercentage(residualCorrosionAllowance?.percentage),
          label: 'Residual Corrosion Allowance',
        },
        {
          value:
            localiseMeasurement({
              unitSystem,
              value: residualThickness?.value ?? 0,
              showUnitLabel: true,
              sourceUnit: UnitsEnum.Meter,
              displayAsUnit: UnitsEnum.Millimeter,
            }) + formatPercentage(residualThickness?.percentage),
          label: 'Residual Thickness',
        },
      ];
    }

    const templateInfo = templateFields?.map((field) => {
      let value = selectedPoiInfo.templateData?.find((td) => td.id === field.id)?.value ?? 'N/A';
      if (field.key === SC_CATEGORY_KEY && (!value || value === 'N/A')) {
        value = selectedPoiInfo.blister?.scCategory || 'N/A';
      }

      if (field.detailFieldKey) {
        const detailField = templateOfSelectedPoi?.fields.find(
          (templateField) => templateField.key === field.detailFieldKey
        );
        const detail = selectedPoiInfo.templateData?.find((td) => td.id === detailField?.id);
        return {
          value,
          label: field?.name || 'N/A',
          type: field?.type,
          valueTooltip: value,
          ...(detail ? { subtitle: detail?.value } : {}),
        };
      }

      if (
        field.type === TemplateFieldTypeEnum.MeasurementPaintLength &&
        selectedPoiInfo?.lineMeasurement?.points
      ) {
        const points = selectedPoiInfo?.lineMeasurement?.points;
        if (!Array.isArray(points) || points.length < 2) {
          value = '';
        }
      }

      return {
        value,
        label: field?.name || 'N/A',
        type: field?.type,
        valueTooltip: value,
        unit: field?.unit,
      };
    });
    return [...blisterInfo, ...templateInfo];
  }, [
    selectedPoiInfo.blister,
    selectedPoiInfo.isAutoGenerated,
    selectedPoiInfo.type,
    selectedPoiInfo.templateData,
    selectedPoiInfo?.lineMeasurement?.points,
    templateFields,
    structureBlisterHeightType,
    unitSystem,
    structureWallLossFactor,
    templateOfSelectedPoi?.fields,
  ]);
  const verifiedFieldsWithValues: Array<{ label: string; value: string }> = useMemo(() => {
    const verifiedData = templateFields?.map((field) => {
      const value =
        selectedPoiInfo.templateData?.find((td) => td.id === field.id)?.verifiedValue ?? 'N/A';

      return {
        value,
        label: field?.name || 'N/A',
      };
    });

    if (selectedPoiInfo?.blister?.verifiedMaxHeight) {
      let verifiedHeight = selectedPoiInfo?.blister.verifiedMaxHeight;
      if (
        selectedPoiInfo?.blister.verifiedMaxHeight.endsWith('"') &&
        unitSystem === UnitSystemEnum.Metric
      ) {
        verifiedHeight =
          convertInchesToMillimeters(
            Number(selectedPoiInfo?.blister.verifiedMaxHeight.slice(0, -1))
          )?.toString() ?? UNCERTAIN_PROFILE_LABEL;
      } else if (
        selectedPoiInfo?.blister.verifiedMaxHeight.endsWith('mm') &&
        unitSystem === UnitSystemEnum.Imperial
      ) {
        verifiedHeight =
          convertMillimetersToInches(
            Number(selectedPoiInfo?.blister.verifiedMaxHeight.slice(0, -2))
          )?.toString() ?? UNCERTAIN_PROFILE_LABEL;
      }

      verifiedData.push({
        label: 'Verified Max Height',
        value: verifiedHeight,
      });
    }
    if (selectedPoiInfo?.blister?.verifiedResidualThickness) {
      let verifiedThickness = selectedPoiInfo?.blister.verifiedResidualThickness;
      if (
        selectedPoiInfo?.blister.verifiedResidualThickness.endsWith('"') &&
        unitSystem === UnitSystemEnum.Metric
      ) {
        verifiedThickness =
          convertInchesToMillimeters(
            Number(selectedPoiInfo?.blister.verifiedResidualThickness.slice(0, -1))
          )?.toString() ?? UNCERTAIN_PROFILE_LABEL;
      } else if (
        selectedPoiInfo?.blister.verifiedResidualThickness.endsWith('mm') &&
        unitSystem === UnitSystemEnum.Imperial
      ) {
        verifiedThickness =
          convertMillimetersToInches(
            Number(selectedPoiInfo?.blister.verifiedResidualThickness.slice(0, -2))
          )?.toString() ?? UNCERTAIN_PROFILE_LABEL;
      }
      verifiedData.push({
        label: 'Verified Residual Thickness',
        value: verifiedThickness,
      });
    }
    return verifiedData;
  }, [
    selectedPoiInfo?.blister?.verifiedMaxHeight,
    selectedPoiInfo?.blister?.verifiedResidualThickness,
    selectedPoiInfo.templateData,
    templateFields,
    unitSystem,
  ]);

  const isInspector = useFeatureFlag('api-inspector');
  const canVerifyData = useFeatureFlag(['ait-engineer', 'client', 'ait-task-lead']);

  // Only show the edit icon for the API Inspector
  // if its a Blister or Pit
  const showEditIconForInspector = useMemo(() => {
    const excludedTypes = [PIT_TYPE, BLISTER_TYPE];
    return excludedTypes.includes(selectedPoiInfo.type) && isInspector;
  }, [isInspector, selectedPoiInfo.type]);

  const isVerified = selectedPoiInfo.isVerified;

  const isUninitialized = useMemo(() => {
    return selectedPoiInfo.type === BLISTER_TYPE && !selectedPoiInfo.blister;
  }, [selectedPoiInfo.blister, selectedPoiInfo.type]);

  const disableDetailsEdit = useMemo(() => {
    if (selectedPoiInfo?.blister?.isManuallyOverridden) {
      return (
        (selectedPoiInfo.isAutoGenerated &&
          selectedPoiInfo.blister.heightType !== BlisterHeightType.BlisterTool) ||
        (!selectedAssemblyId && !checkByType(selectedPoiInfo.type)) ||
        (disableEdit && !!selectedPoiInfo.blister) ||
        isUninitialized
      );
    } else {
      return (
        (selectedPoiInfo.isAutoGenerated &&
          structureBlisterHeightType !== BlisterHeightType.BlisterTool) ||
        (!selectedAssemblyId && !checkByType(selectedPoiInfo.type)) ||
        (disableEdit && !!selectedPoiInfo.blister) ||
        isUninitialized
      );
    }
  }, [
    selectedPoiInfo.blister,
    selectedPoiInfo.isAutoGenerated,
    selectedPoiInfo.type,
    selectedAssemblyId,
    checkByType,
    disableEdit,
    isUninitialized,
    structureBlisterHeightType,
  ]);

  return (
    <Box sx={{ ...boxRootStyle, height: `calc(100vh - ${navbarHeight + 140}px)` }}>
      <MeasureAutoPointOfInterest pointOfInterest={selectedPoiInfo} />
      {canVerifyData && selectedPoiInfo.isAutoGenerated && selectedPoiInfo.blister && (
        <BlisterHeightMethodSelector pointOfInterest={selectedPoiInfo} key={selectedPoiInfo.id} />
      )}
      {/* If an AIT member sees a POI thats not verified by an inspector yet */}
      {!isInspector && !isVerified ? (
        <AitUserVerifiedPoiView
          pointOfInterest={selectedPoiInfo}
          fields={templateFieldsWithValues}
          template={templateOfSelectedPoi as PointOfInterestTemplate}
          disableEdit={disableDetailsEdit}
        />
      ) : (
        <GenericAccordion
          summary="Pre Inspection Data"
          summaryStyles={{ fontWeight: 500 }}
          openByDefault
          summaryIcon={
            showEditIconForInspector ? (
              <InspectorBlisterEditIconButton
                pointOfInterest={selectedPoiInfo}
                template={templateOfSelectedPoi as PointOfInterestTemplate}
              />
            ) : (
              <></>
            )
          }
          {...accordianProps}
        >
          <FieldViewer fields={templateFieldsWithValues} />
        </GenericAccordion>
      )}

      {canVerifyData && isVerified && (
        <>
          <Divider />
          <InspectorVerifiedPoiView
            fields={verifiedFieldsWithValues}
            pointOfInterest={selectedPoiInfo}
            template={templateOfSelectedPoi as PointOfInterestTemplate}
            disableEdit={disableDetailsEdit}
          />
        </>
      )}

      {canVerifyData && !isVerified && (
        <InspectorVerifiedFieldsAccordian
          canVerifyData={canVerifyData}
          fields={verifiedFieldsWithValues}
          accordianProps={accordianProps}
          pointOfInterest={selectedPoiInfo}
          template={templateOfSelectedPoi as PointOfInterestTemplate}
        />
      )}
      <Divider />

      {attachments.length > 0 && (
        <Typography variant="h6" sx={{ mb: 2, px: 2 }}>
          Attachments
        </Typography>
      )}
      <AttachmentsViewer
        attachments={attachments}
        setAttachments={setAttachments}
        selectedPoiInfo={selectedPoiInfo}
      />
    </Box>
  );
};
